import { useSyncExternalStore } from 'react'
import type { SupportSessionRecord } from './api'

const SUPPORT_SESSION_STORAGE_KEY = 'dd_support_session'
const SUPPORT_SESSION_EVENT = 'dd-support-session-changed'

export const SUPPORT_READ_ONLY_EXPLANATION = 'Read-only support view — sign in as this tenant to take action.'
export const SUPPORT_READ_ONLY_NOTICE_ID = 'support-read-only-notice'

export type SupportSessionContext = {
  sessionId?: string
  targetTenantOrgId?: string
  reason?: string
  startedAt?: string
  expiresAt?: string
  status?: SupportSessionRecord['status']
  readOnly?: boolean
}

function isBrowser() {
  return typeof window !== 'undefined' && typeof localStorage !== 'undefined'
}

function parseSupportContext(raw: string | null): SupportSessionContext | null {
  if (!raw) return null
  try {
    return JSON.parse(raw) as SupportSessionContext
  } catch {
    return null
  }
}

export function readSupportSessionContext(): SupportSessionContext | null {
  if (!isBrowser()) return null
  return parseSupportContext(localStorage.getItem(SUPPORT_SESSION_STORAGE_KEY))
}

function notifySupportSessionChanged() {
  if (!isBrowser()) return
  window.dispatchEvent(new Event(SUPPORT_SESSION_EVENT))
}

export function writeSupportContext(session: SupportSessionRecord | null) {
  if (!isBrowser()) return
  if (!session) {
    if (!localStorage.getItem(SUPPORT_SESSION_STORAGE_KEY)) return
    localStorage.removeItem(SUPPORT_SESSION_STORAGE_KEY)
    notifySupportSessionChanged()
    return
  }

  const nextRaw = JSON.stringify({
    sessionId: session.id,
    targetTenantOrgId: session.target_tenant_org_id,
    reason: session.reason,
    startedAt: session.started_at,
    expiresAt: session.expires_at,
    status: session.status,
    readOnly: session.read_only,
  })
  if (localStorage.getItem(SUPPORT_SESSION_STORAGE_KEY) === nextRaw) return
  localStorage.setItem(SUPPORT_SESSION_STORAGE_KEY, nextRaw)
  notifySupportSessionChanged()
}

export function clearSupportContext() {
  writeSupportContext(null)
}

function subscribe(callback: () => void) {
  if (!isBrowser()) return () => {}

  const onStorage = (event: StorageEvent) => {
    if (event.key === SUPPORT_SESSION_STORAGE_KEY || event.key === null) {
      callback()
    }
  }

  const onSupportSessionChanged = () => callback()

  window.addEventListener('storage', onStorage)
  window.addEventListener(SUPPORT_SESSION_EVENT, onSupportSessionChanged)

  return () => {
    window.removeEventListener('storage', onStorage)
    window.removeEventListener(SUPPORT_SESSION_EVENT, onSupportSessionChanged)
  }
}

function getSnapshot() {
  if (!isBrowser()) return null
  return localStorage.getItem(SUPPORT_SESSION_STORAGE_KEY)
}

export function isSupportReadOnlySession(session: SupportSessionContext | null | undefined) {
  if (!session?.sessionId) return false
  if (session.readOnly === false) return false
  if (session.status === 'ENDED' || session.status === 'EXPIRED') return false
  return true
}

export function useSupportSessionContext() {
  const raw = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
  return parseSupportContext(raw)
}

export function useSupportReadOnlyView() {
  const supportSession = useSupportSessionContext()
  const isSupportReadOnlyView = isSupportReadOnlySession(supportSession)

  return {
    supportSession,
    isSupportReadOnlyView,
    supportReadOnlyExplanation: SUPPORT_READ_ONLY_EXPLANATION,
    supportReadOnlyNoticeId: SUPPORT_READ_ONLY_NOTICE_ID,
  }
}

export function getSupportReadOnlyControlProps(isSupportReadOnlyView: boolean) {
  if (!isSupportReadOnlyView) return {}
  return {
    title: SUPPORT_READ_ONLY_EXPLANATION,
    'aria-describedby': SUPPORT_READ_ONLY_NOTICE_ID,
  }
}

export function SupportReadOnlyNotice({
  className = '',
}: {
  className?: string
}) {
  const { isSupportReadOnlyView } = useSupportReadOnlyView()

  if (!isSupportReadOnlyView) return null

  return (
    <div
      id={SUPPORT_READ_ONLY_NOTICE_ID}
      role="note"
      className={`rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 ${className}`.trim()}
    >
      {SUPPORT_READ_ONLY_EXPLANATION}
    </div>
  )
}
